import type { Player } from "@/types";
import { trackEvent } from "@/lib/audit";
import { t, getStoredLang } from "@/lib/i18n";

export interface CommentaryFrame {
  t: number;
  minute: number;
  ball: { x: number; y: number };
  players: Player[];
  inDanger: boolean;
  isShot?: boolean;
  isGoal?: boolean;
  freeKick?: boolean;
  attackingMex?: boolean;
  momentum?: number;
  xgPct?: number;
  homeCode?: string;
  awayCode?: string;
}

export interface LiveCommentary {
  id: string;
  text: string;
  kind:
    | "dangerous_attack"
    | "shot"
    | "goal"
    | "freekick"
    | "pressure"
    | "var"
    | "stat"
    | "player_focus";
  priority: number;
  player?: string;
  at: number;
}

function pickKey(keys: readonly string[], seed: number): string {
  return keys[Math.abs(seed) % keys.length];
}

function nearestAttacker(frame: CommentaryFrame): Player | undefined {
  const team = frame.attackingMex ? "MEX" : "ARG";
  const candidates = frame.players.filter((p) => p.team === team && p.role !== "GK");
  if (!candidates.length) return undefined;
  return candidates.reduce<Player | undefined>((best, p) => {
    const d = Math.hypot(p.x - frame.ball.x, p.y - frame.ball.y);
    if (!best) return p;
    const bd = Math.hypot(best.x - frame.ball.x, best.y - frame.ball.y);
    return d < bd ? p : best;
  }, undefined);
}

function describePlayer(p?: Player): string {
  const lang = getStoredLang();
  if (!p?.name) {
    if (!p) return t("lc_attacker_default", lang);
    if (p.role === "FWD") return t("lc_role_fwd", lang);
    if (p.role === "MID") return t("lc_role_mid", lang);
    if (p.role === "DEF") return t("lc_role_def", lang);
    return t("lc_role_player", lang);
  }
  return p.name;
}

/** Stateful generator: keeps a per-instance cooldown + last text to avoid repeats. */
export class LiveCommentaryEngine {
  private lastEmit = 0;
  private recentTexts: string[] = [];
  private minIntervalMs = 18_000;
  private maxIntervalMs = 40_000;

  reset() {
    this.lastEmit = 0;
    this.recentTexts = [];
  }

  tick(frame: CommentaryFrame, nowMs: number = Date.now()): LiveCommentary | null {
    const elapsedSinceLast = nowMs - this.lastEmit;
    const hasUrgent =
      frame.isGoal ||
      (frame.xgPct ?? 0) >= 0.55 ||
      (frame.freeKick && frame.ball.x > 18) ||
      frame.isShot;

    if (!hasUrgent && elapsedSinceLast < this.minIntervalMs) return null;
    if (elapsedSinceLast < 6_000) return null; // hard floor
    if (!hasUrgent && elapsedSinceLast > this.maxIntervalMs) {
      // force a stat
    } else if (!hasUrgent && elapsedSinceLast < this.maxIntervalMs && Math.random() < 0.4) {
      return null;
    }

    const attacker = nearestAttacker(frame);
    const playerName = describePlayer(attacker);
    const lang = getStoredLang();
    let kind: LiveCommentary["kind"] = "stat";
    let text = "";
    let priority = 1;

    if (frame.isGoal) {
      kind = "goal";
      priority = 5;
      text = t("lc_goal", lang, { player: playerName });
    } else if ((frame.xgPct ?? 0) >= 0.55) {
      kind = "shot";
      priority = 4;
      text = t(
        pickKey(["lc_shot_1", "lc_shot_2", "lc_shot_3"], frame.minute + Math.floor(frame.t)),
        lang,
        { player: playerName },
      );
    } else if (frame.freeKick && frame.ball.x > 18) {
      kind = "freekick";
      priority = 3;
      text = t("lc_freekick", lang, { player: playerName });
    } else if (frame.inDanger) {
      kind = "dangerous_attack";
      priority = 3;
      text = t(
        pickKey(["lc_danger_1", "lc_danger_2", "lc_danger_3"], frame.minute + Math.floor(frame.t / 3)),
        lang,
        { player: playerName },
      );
    } else {
      // pressure / stat commentary
      const attackers = frame.players.filter(
        (p) => p.team === (frame.attackingMex ? "MEX" : "ARG") && p.x * (frame.attackingMex ? 1 : -1) > 5,
      ).length;
      if (attackers >= 5 && (frame.momentum ?? 0) > 0.5) {
        kind = "pressure";
        priority = 2;
        const teamLabel = frame.attackingMex
          ? (frame.homeCode ?? t("team_mex", lang))
          : (frame.awayCode ?? t("team_arg", lang));
        text = t("lc_pressure", lang, { team: teamLabel, n: attackers });
      } else {
        kind = "stat";
        priority = 1;
        text = t(
          pickKey(
            ["lc_stat_1", "lc_stat_2", "lc_stat_3", "lc_stat_4"],
            frame.minute + Math.floor(frame.t / 5),
          ),
          lang,
          { minute: frame.minute },
        );
      }
    }

    if (this.recentTexts.includes(text)) return null;
    this.recentTexts = [...this.recentTexts.slice(-4), text];
    this.lastEmit = nowMs;

    void trackEvent("live_commentary_generated", {
      kind,
      minute: frame.minute,
      player: attacker?.name ?? null,
      priority,
    });
    if (attacker?.name) {
      void trackEvent("player_identified", { name: attacker.name, surface: "commentary" });
    }

    return {
      id: `lc-${nowMs}`,
      text,
      kind,
      priority,
      player: attacker?.name,
      at: frame.minute,
    };
  }
}
